import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { notificationsAPI } from '../services/api.js';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toasts, setToasts] = useState([]);
  
  const { isAuthenticated } = useAuth();
  const { subscribe, unsubscribe, isConnected } = useWebSocket();
  const pendingMarkAsRead = useRef(new Set());

  // Load notifications and stats
  const loadNotifications = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError('');
      
      const [notificationsData, statsData] = await Promise.all([
        notificationsAPI.getNotifications(params),
        notificationsAPI.getStats()
      ]);
      
      const processedNotifications = (notificationsData || []).map(notification => ({
        ...notification,
        notification_type: notification.notification_type || notification.type || 'info',
        status: notification.status || 'unread',
        title: notification.title || 'Уведомление',
        message: notification.message || '',
        created_at: notification.created_at || new Date().toISOString(),
        read_at: notification.read_at || null
      }));
      
      setNotifications(processedNotifications);
      setUnreadCount(statsData.unread || 0);
      
      return { success: true, notifications: processedNotifications, stats: statsData };
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError('Не удалось загрузить уведомления');
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const calculateLocalUnreadCount = useCallback((notifs) => {
    return notifs.filter(notif => notif.status === 'unread').length;
  }, []);

  // WebSocket handlers
  const handleInitialNotifications = useCallback((data) => {
    // data - массив уведомлений
    setNotifications(prev => {
      const existingIds = new Set(prev.map(n => n.id));
      const newOnes = data.filter(n => !existingIds.has(n.id));
      const merged = [...newOnes, ...prev];
      merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setUnreadCount(calculateLocalUnreadCount(merged));
      return merged;
    });
  }, [calculateLocalUnreadCount]);

  const handleNewNotification = useCallback((data) => {
    const newNotification = {
      ...data,
      notification_type: data.notification_type || data.type || 'info',
      status: 'unread'
    };
    
    setNotifications(prev => {
      const updated = [newNotification, ...prev];
      setUnreadCount(calculateLocalUnreadCount(updated));
      return updated;
    });
    
    showToast({
      id: Date.now() + Math.random(),
      title: newNotification.title,
      message: newNotification.message,
      type: newNotification.notification_type,
      notificationId: newNotification.id
    });
  }, [calculateLocalUnreadCount]);

  const handleNotificationRead = useCallback((data) => {
    setNotifications(prev => {
      const updated = prev.map(notif => 
        notif.id === data.notification_id 
          ? { ...notif, status: 'read', read_at: data.read_at }
          : notif
      );
      setUnreadCount(calculateLocalUnreadCount(updated));
      return updated;
    });
  }, [calculateLocalUnreadCount]);

  const handleAllNotificationsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(notif => ({
        ...notif,
        status: 'read',
        read_at: notif.read_at || new Date().toISOString()
      }))
    );
    setUnreadCount(0);
  }, []);

  // Subscribe to WebSocket events
  useEffect(() => {
    if (!isConnected) return;

    subscribe('initial_notifications', handleInitialNotifications);
    subscribe('notification', handleNewNotification);
    subscribe('notification_read', (data) => handleNotificationRead(data));
    subscribe('all_notifications_read', handleAllNotificationsRead);

    return () => {
      unsubscribe('initial_notifications', handleInitialNotifications);
      unsubscribe('notification', handleNewNotification);
      unsubscribe('notification_read', handleNotificationRead);
      unsubscribe('all_notifications_read', handleAllNotificationsRead);
    };
  }, [isConnected, subscribe, unsubscribe, handleInitialNotifications, handleNewNotification, handleNotificationRead, handleAllNotificationsRead]);

  // Load notifications on auth
  useEffect(() => {
    if (isAuthenticated) {
      loadNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
      setToasts([]);
    }
  }, [isAuthenticated, loadNotifications]);

  const markAsRead = async (notificationId) => {
    if (pendingMarkAsRead.current.has(notificationId)) {
      return { success: false, error: 'Уже в процессе отметки' };
    }

    pendingMarkAsRead.current.add(notificationId);

    try {
      setError('');
      
      setNotifications(prev => {
        const updated = prev.map(notif => 
          notif.id === notificationId && notif.status === 'unread'
            ? { ...notif, status: 'read', read_at: new Date().toISOString() }
            : notif
        );
        setUnreadCount(calculateLocalUnreadCount(updated));
        return updated;
      });

      const result = await notificationsAPI.markAsRead(notificationId);
      
      setNotifications(prev => {
        const updated = prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, status: 'read', read_at: result.read_at || new Date().toISOString() }
            : notif
        );
        setUnreadCount(calculateLocalUnreadCount(updated));
        return updated;
      });
      
      return { success: true, notification: result };
    } catch (err) {
      console.error('Error marking notification as read:', err);
      
      setNotifications(prev => {
        const updated = prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, status: 'unread', read_at: null }
            : notif
        );
        setUnreadCount(calculateLocalUnreadCount(updated));
        return updated;
      });
      
      setError('Не удалось отметить уведомление как прочитанное');
      return { success: false, error: err.message };
    } finally {
      pendingMarkAsRead.current.delete(notificationId);
    }
  };

  const markAllAsRead = async () => {
    try {
      setError('');
      
      setNotifications(prev => 
        prev.map(notif => ({
          ...notif,
          status: 'read',
          read_at: notif.read_at || new Date().toISOString()
        }))
      );
      setUnreadCount(0);

      const result = await notificationsAPI.markAllAsRead();
      
      await loadNotifications();
      
      return { success: true, result };
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      
      await loadNotifications();
      
      setError('Не удалось отметить все уведомления как прочитанные');
      return { success: false, error: err.message };
    }
  };

  const showToast = (toastData) => {
    setToasts(prev => [...prev, toastData]);
    
    setTimeout(() => {
      removeToast(toastData.id);
    }, 8000);
  };

  const removeToast = (toastId) => {
    setToasts(prev => prev.filter(toast => toast.id !== toastId));
  };

  const sendTestNotification = async (userId, title, message) => {
    try {
      const result = await notificationsAPI.testWebSocket(userId, title, message);
      return { success: true, result };
    } catch (err) {
      console.error('Error sending test notification:', err);
      return { success: false, error: err.message };
    }
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    toasts,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    sendTestNotification,
    removeToast,
    isWsConnected: isConnected,
    wsState: isConnected ? 'connected' : 'disconnected',
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};